// ============================================================================
// auth — oidcClient
// Owner: Claude 3 (Frontend & UX).
// Purpose: OIDC code-flow + PKCE handshake wrapper. Browser-side stores only
//          the OPAQUE codeVerifierId issued by auth-svc; the verifier itself
//          never leaves the server (a malicious extension cannot replay it).
//
// SESSION STORAGE CHOICE: we store the eDMS-issued accessToken + refreshToken
// in sessionStorage and bind every API request with DPoP (SEC-023). The
// httpOnly-cookie alternative is stronger against XSS but does not work for
// the DPoP proof-of-possession story (the SPA needs the token to sign DPoP
// JWTs). The tradeoff: XSS in this SPA can read the tokens — therefore the
// CSP must be strict (`script-src 'self'` only, no inline) and the WAF
// blocks reflected XSS at the gateway.
//
// URS rows: AUTH-001..008, SEC-023.
// ============================================================================
import { apiFetch } from '../api/client';

const STORAGE_KEYS = {
  access: 'edms.session.access',
  refresh: 'edms.session.refresh',
  expires: 'edms.session.expires',
  userId: 'edms.session.userId',
  tenantId: 'edms.session.tenantId',
  preferredUsername: 'edms.session.preferredUsername',
  roles: 'edms.session.roles',
  state: 'edms.oidc.state',
  nonce: 'edms.oidc.nonce',
  codeVerifierId: 'edms.oidc.codeVerifierId',
  returnTo: 'edms.oidc.returnTo',
} as const;

export type Session = {
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
  userId: string;
  tenantId: string;
  preferredUsername: string | null;
  roles: string[];
};

type LoginResponse = {
  authorizationUrl: string;
  state: string;
  nonce: string;
  codeVerifierId: string;
};

type TokenResponse = {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresInSeconds: number;
  issuedAt: string;
  userId: string;
  tenantId: string;
  preferredUsername: string | null;
  roles: string[];
};

export async function initiateLogin(returnTo: string): Promise<void> {
  const resp = await apiFetch<LoginResponse>(`/api/v1/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
  sessionStorage.setItem(STORAGE_KEYS.state, resp.state);
  sessionStorage.setItem(STORAGE_KEYS.nonce, resp.nonce);
  sessionStorage.setItem(STORAGE_KEYS.codeVerifierId, resp.codeVerifierId);
  sessionStorage.setItem(STORAGE_KEYS.returnTo, returnTo);
  window.location.assign(resp.authorizationUrl);
}

export async function completeLogin(code: string, state: string): Promise<Session> {
  const storedState = sessionStorage.getItem(STORAGE_KEYS.state);
  const codeVerifierId = sessionStorage.getItem(STORAGE_KEYS.codeVerifierId);
  if (!storedState || !codeVerifierId) {
    throw new Error('No pending OIDC flow — sessionStorage missing state/codeVerifierId');
  }
  if (storedState !== state) {
    // CSRF defense — if the state we issued doesn't match, refuse the callback.
    throw new Error('OIDC state mismatch — possible CSRF attempt');
  }
  const tokens = await apiFetch<TokenResponse>('/api/v1/auth/callback', {
    method: 'POST',
    body: { code, state, codeVerifierId },
  });
  const session = persistSession(tokens);
  // One-time bits consumed.
  sessionStorage.removeItem(STORAGE_KEYS.state);
  sessionStorage.removeItem(STORAGE_KEYS.nonce);
  sessionStorage.removeItem(STORAGE_KEYS.codeVerifierId);
  return session;
}

export async function refreshSession(): Promise<Session> {
  const refresh = sessionStorage.getItem(STORAGE_KEYS.refresh);
  if (!refresh) throw new Error('No refresh token in sessionStorage');
  const tokens = await apiFetch<TokenResponse>('/api/v1/auth/refresh', {
    method: 'POST',
    headers: { 'X-Refresh-Token': refresh },
  });
  return persistSession(tokens);
}

export async function logout(): Promise<void> {
  const access = sessionStorage.getItem(STORAGE_KEYS.access);
  const refresh = sessionStorage.getItem(STORAGE_KEYS.refresh);
  try {
    await apiFetch<void>('/api/v1/auth/logout', {
      method: 'POST',
      headers: {
        ...(access ? { Authorization: `Bearer ${access}` } : {}),
        ...(refresh ? { 'X-Refresh-Token': refresh } : {}),
      },
    });
  } finally {
    Object.values(STORAGE_KEYS).forEach((k) => sessionStorage.removeItem(k));
  }
}

export function readStoredSession(): Session | null {
  const access = sessionStorage.getItem(STORAGE_KEYS.access);
  const refresh = sessionStorage.getItem(STORAGE_KEYS.refresh);
  const expires = sessionStorage.getItem(STORAGE_KEYS.expires);
  const userId = sessionStorage.getItem(STORAGE_KEYS.userId);
  const tenantId = sessionStorage.getItem(STORAGE_KEYS.tenantId);
  if (!access || !refresh || !expires || !userId || !tenantId) return null;
  return {
    accessToken: access,
    refreshToken: refresh,
    expiresAt: Number(expires),
    userId,
    tenantId,
    preferredUsername: sessionStorage.getItem(STORAGE_KEYS.preferredUsername),
    roles: parseRolesSafely(sessionStorage.getItem(STORAGE_KEYS.roles)),
  };
}

/**
 * Defensive: a prior bug wrote `JSON.stringify(undefined) === 'undefined'`
 * into sessionStorage[roles], which then crashed JSON.parse on hydrate.
 * This helper accepts null / 'undefined' / malformed JSON and returns an
 * empty array rather than throwing.
 */
function parseRolesSafely(raw: string | null): string[] {
  if (!raw || raw === 'undefined' || raw === 'null') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function consumeReturnTo(): string {
  const r = sessionStorage.getItem(STORAGE_KEYS.returnTo) ?? '/';
  sessionStorage.removeItem(STORAGE_KEYS.returnTo);
  return r;
}

function persistSession(t: TokenResponse): Session {
  const expiresAt = Date.now() + t.expiresInSeconds * 1000;
  sessionStorage.setItem(STORAGE_KEYS.access, t.accessToken);
  sessionStorage.setItem(STORAGE_KEYS.refresh, t.refreshToken);
  sessionStorage.setItem(STORAGE_KEYS.expires, String(expiresAt));
  sessionStorage.setItem(STORAGE_KEYS.userId, t.userId);
  // Defensive: every setItem guards against undefined so a malformed
  // /auth/callback response can never poison the storage with the literal
  // string 'undefined' (which then fails JSON.parse on next hydrate).
  if (t.tenantId) sessionStorage.setItem(STORAGE_KEYS.tenantId, t.tenantId);
  if (t.preferredUsername) sessionStorage.setItem(STORAGE_KEYS.preferredUsername, t.preferredUsername);
  sessionStorage.setItem(STORAGE_KEYS.roles, JSON.stringify(Array.isArray(t.roles) ? t.roles : []));
  return {
    accessToken: t.accessToken,
    refreshToken: t.refreshToken,
    expiresAt,
    userId: t.userId,
    tenantId: t.tenantId,
    preferredUsername: t.preferredUsername,
    roles: t.roles,
  };
}
